/**
 * Best Logo Mats — UTM capture + web-intake form submission.
 * Implements docs/BMOS_BEST_LOGO_MATS_WEBSITE_REBUILD.md §4.5 exactly:
 * first-touch UTM capture in sessionStorage, source-mapping table, campaign
 * fallback to industries/{slug}. Submits via the Shopify app proxy to
 * BMOS's existing POST /public/web-intake/lead (already built and deployed
 * to dev — see src/modules/webintake/routes.ts).
 *
 * [VERIFY] the real app-proxy subpath once the custom app is created
 * (docs/BMOS_BLM_SHOPIFY_IMPLEMENTATION_READINESS.md §6) — placeholder
 * below is /apps/bmos/lead per that doc's recommendation.
 */
(function () {
  var PROXY_PATH = '/apps/bmos/lead';
  var STORAGE_KEY = 'blm_attribution';

  function captureUtmOnce() {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    var params = new URLSearchParams(window.location.search);
    var utmSource = params.get('utm_source');
    var utmCampaign = params.get('utm_campaign');
    var referrer = document.referrer || '';
    var isSameOrigin = referrer.indexOf(window.location.hostname) !== -1;

    var source = mapSource(utmSource, referrer, isSameOrigin);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      source: source,
      campaign: utmCampaign || null,
      firstTouchAt: new Date().toISOString(),
    }));
  }

  // Mapping table per main doc §4.5 — deliberately degrades anything
  // unrecognized to a known-good value rather than passing raw utm_source
  // through (the endpoint's own SOURCE_VALUES enum would reject it anyway).
  function mapSource(utmSource, referrer, isSameOrigin) {
    if (utmSource) {
      var v = utmSource.toLowerCase();
      if (v === 'google' || v === 'adwords' || v === 'googleads') return 'google';
      if (v === 'meta' || v === 'facebook' || v === 'fb' || v === 'instagram' || v === 'ig') return 'meta';
      return 'unknown';
    }
    if (!referrer || isSameOrigin) {
      // No UTM, no external referrer — treat as organic if it looks like a
      // search engine, else unknown. A precise search-engine referrer check
      // is a light heuristic, not a hard requirement — [VERIFY]/refine once
      // real analytics data is available.
      var looksLikeSearchEngine = /google\.|bing\.|duckduckgo\.|yahoo\./i.test(referrer);
      return looksLikeSearchEngine ? 'organic' : 'unknown';
    }
    return 'referral';
  }

  function getAttribution() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  /**
   * Wires a <form data-blm-webintake> — expects fields name/email/phone/
   * message and a data-industry attribute on the form for the campaign
   * fallback. Honeypot field must be named company_website and hidden via
   * CSS ONLY (never type="hidden"/display:none) — see main doc §4.2.
   */
  function wireForm(form) {
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      var attribution = getAttribution();
      var industry = form.getAttribute('data-industry') || '';
      var submitBtn = form.querySelector('[type="submit"]');
      // Real bug found live 2026-07-05: data-blm-success is a sibling of
      // the form (has to be, since the form itself gets hidden on success
      // and the message needs to stay visible) — form.querySelector() only
      // searches descendants, so it always returned null and no customer
      // ever saw a success confirmation. Search from the shared wrapper
      // instead (falls back to parentElement for safety if the wrapper
      // isn't present for some reason).
      var scope = form.closest('[data-blm-webintake-wrap]') || form.parentElement || form;
      var errorEl = form.querySelector('[data-blm-error]');
      var successEl = scope.querySelector('[data-blm-success]');
      if (errorEl) errorEl.hidden = true;

      var payload = {
        name: form.querySelector('[name="name"]').value,
        email: form.querySelector('[name="email"]').value,
        phone: (form.querySelector('[name="phone"]') || {}).value || '',
        industry: industry,
        source: attribution.source || 'unknown',
        campaign: attribution.campaign || ('industries/' + industry),
        message: (form.querySelector('[name="message"]') || {}).value || '',
        company_website: (form.querySelector('[name="company_website"]') || {}).value || '',
      };

      if (submitBtn) submitBtn.disabled = true;

      fetch(PROXY_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) { return res.json().then(function (body) { return { ok: res.ok, body: body }; }); })
        .then(function (result) {
          if (result.body && result.body.ok) {
            form.hidden = true;
            if (successEl) successEl.hidden = false;
          } else {
            if (errorEl) {
              errorEl.textContent = (result.body && result.body.error) || 'Please check your information and try again.';
              errorEl.hidden = false;
            }
            if (submitBtn) submitBtn.disabled = false;
          }
        })
        .catch(function () {
          if (errorEl) {
            errorEl.textContent = 'Something went wrong — please try again in a moment.';
            errorEl.hidden = false;
          }
          if (submitBtn) submitBtn.disabled = false;
        });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    captureUtmOnce();
    document.querySelectorAll('[data-blm-webintake]').forEach(wireForm);
  });
})();
